import { useState, useCallback, useEffect, useRef } from 'react';
import type { AiDraft } from '@roll1/shared';
import type { ServerMessage } from '@roll1/shared';
import { useSessionContext } from '../contexts/SessionContext';

/**
 * Return surface of {@link useAiDrafts}. Consumers (e.g. DraftTray) read the
 * hydrated draft list and issue lifecycle commands (accept / reject / revert)
 * that map onto the ai:draft-* WS protocol.
 */
export interface UseAiDraftsReturn {
  /** All drafts known to the client for the (optionally) scoped conversation. */
  readonly drafts: readonly AiDraft[];
  /** Number of scoped drafts still pending review (matches the collapsed AI column badge, FR-07). */
  readonly pendingCount: number;
  /** Accept a batch of pending drafts — sends ai:draft-accept. */
  readonly accept: (draftIds: readonly string[]) => void;
  /** Reject a batch of pending drafts — sends ai:draft-reject. */
  readonly reject: (draftIds: readonly string[]) => void;
  /** Revert a single applied draft — sends ai:draft-revert. */
  readonly revert: (draftId: string) => void;
}

/**
 * Hydrates AI drafts from mp.serverState.aiDrafts and keeps them fresh by
 * idempotently subscribing to ai:draft-created / ai:draft-updated echoes.
 *
 * Lesson from bits (beats): the subscription is idempotent — the same draft
 * arriving twice replaces rather than duplicates (keyed by id). Local mutations
 * (accept/reject/revert) are optimistic: we update status immediately and let
 * the server echo confirm (or an ai:draft-updated correct) the change.
 *
 * `conversationId`, when provided, scopes the visible drafts to that
 * conversation. It may be a single id (chat scope) or an array of ids
 * (multi-scope — e.g. chat + `digest:<sessionId>` so the GM can see digest
 * drafts alongside the live chat, R1). Undefined = show all drafts in state.
 */
function isInScope(
  conversationId: string,
  scope: string | readonly string[] | undefined,
): boolean {
  if (scope === undefined) {
    return true;
  }
  if (typeof scope === 'string') {
    return conversationId === scope;
  }
  return scope.includes(conversationId);
}

export function useAiDrafts(
  conversationId?: string | readonly string[],
): UseAiDraftsReturn {
  const { mp } = useSessionContext();

  const readServerDrafts = useCallback((): readonly AiDraft[] => {
    const serverState = mp.serverState as { aiDrafts?: readonly AiDraft[] } | undefined;
    return serverState?.aiDrafts ?? [];
  }, [mp]);

  const [drafts, setDrafts] = useState<readonly AiDraft[]>(() => readServerDrafts());

  // Keep a ref of the latest conversationId for use inside the WS closure so we
  // can filter echoes without re-subscribing on every id change.
  const conversationIdRef = useRef<string | readonly string[] | undefined>(
    conversationId,
  );
  useEffect((): void => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  /** Idempotent upsert: replace by id if present, otherwise append. */
  const upsertDraft = useCallback((draft: AiDraft): void => {
    setDrafts((prev: readonly AiDraft[]): readonly AiDraft[] => {
      const index = prev.findIndex((d: AiDraft): boolean => d.id === draft.id);
      if (index === -1) {
        return [...prev, draft];
      }
      const next = prev.slice();
      next[index] = draft;
      return next;
    });
  }, []);

  // Re-hydrate from server state when it changes (e.g. reconnect / snapshot).
  useEffect((): void => {
    setDrafts(readServerDrafts());
  }, [readServerDrafts]);

  // Subscribe to ai:draft-* echoes. Idempotent by id — see upsertDraft.
  useEffect((): (() => void) => {
    const unsub = mp.onMessage((msg: ServerMessage): void => {
      if (msg.type !== 'ai:draft-created' && msg.type !== 'ai:draft-updated') {
        return;
      }
      const { draft } = msg as { draft: AiDraft };
      if (!isInScope(draft.conversationId, conversationIdRef.current)) {
        return;
      }
      upsertDraft(draft);
    });
    return unsub;
  }, [mp, upsertDraft]);

  const scopedDrafts = conversationId === undefined
    ? drafts
    : drafts.filter((d: AiDraft): boolean =>
        isInScope(d.conversationId, conversationId),
      );

  const pendingCount = scopedDrafts.filter((d: AiDraft): boolean => d.status === 'pending').length;

  /** Optimistically patch local draft statuses, then send the command. */
  const patchStatus = useCallback(
    (draftIds: readonly string[], status: AiDraft['status']): void => {
      const idSet = new Set<string>(draftIds);
      setDrafts((prev: readonly AiDraft[]): readonly AiDraft[] =>
        prev.map((d: AiDraft): AiDraft =>
          idSet.has(d.id) ? { ...d, status } : d,
        ),
      );
    },
    [],
  );

  const accept = useCallback(
    (draftIds: readonly string[]): void => {
      if (draftIds.length === 0) {
        return;
      }
      patchStatus(draftIds, 'applied');
      mp.send({ type: 'ai:draft-accept', draftIds: [...draftIds] });
    },
    [mp, patchStatus],
  );

  const reject = useCallback(
    (draftIds: readonly string[]): void => {
      if (draftIds.length === 0) {
        return;
      }
      patchStatus(draftIds, 'rejected');
      mp.send({ type: 'ai:draft-reject', draftIds: [...draftIds] });
    },
    [mp, patchStatus],
  );

  const revert = useCallback(
    (draftId: string): void => {
      patchStatus([draftId], 'reverted');
      mp.send({ type: 'ai:draft-revert', draftId });
    },
    [mp, patchStatus],
  );

  return { drafts: scopedDrafts, pendingCount, accept, reject, revert };
}